import {
  Box,
  Button,
  Flex,
  Switch,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import DisabledOverlay from 'components/DisabledOverlay';
import {AnotherNumberInput, PercentageNumberInput, TwNumberInput} from 'components/NumberInput';
import { get as _get, cloneDeep } from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { firstOfMonthIn } from 'helpers/dateHelper';

const tableConfig = [
  // {
  //   title: 'Direct Marketing',
  //   propName: 'directMarketing',
  //   dataPath: 'profit_and_loss.direct_marketing_spend',
  //   element: AnotherNumberInput,
  // },
  // {
  //   title: 'Draw',
  //   propName: 'draw',
  //   dataPath: 'twelve_balance.draw',
  //   element: AnotherNumberInput,
  // },
  {
    title: 'Existing Cohort Revenue',
    propName: 'existingCohortRevenue',
    dataPath: 'profit_and_loss.revenue_from_existing_cohorts',
    element: AnotherNumberInput,
  },
  {
    title: 'Cumulative ROAS (%)',
    propName: 'roas',
    dataPath: 'roas',
    element: PercentageNumberInput,
    indexDataStart: 0,
    precision: 2,
  },
  {
    title: 'Gross Profit Margin (%)',
    propName: 'grossProfitMargin',
    dataPath: 'profit_and_loss.gross_profit_margin',
    element: PercentageNumberInput,
    precision: 2,
  },
  {
    title: 'Overhead',
    propName: 'overhead',
    dataPath: 'profit_and_loss.overhead',
    element: AnotherNumberInput,
  },
  {
    title: 'Payroll',
    propName: 'payroll',
    dataPath: 'profit_and_loss.payroll',
    element: AnotherNumberInput,
  },
  {
    title: 'Non-Operating Income',
    propName: 'nonOperatingIncome',
    dataPath: 'profit_and_loss.non_operating_income',
    element: AnotherNumberInput,
  },
  {
    title: 'Non-Operating Expenses',
    propName: 'nonOperatingExpenses',
    dataPath: 'profit_and_loss.non_operating_expenses',
    element: AnotherNumberInput,
  },
];

const CashflowModelDrivers = forwardRef(
  ({ modelResults, onDriversChange, roasVector, initialFactors, useInitialFactors, predictionSize }, ref) => {
    const [drivers, setDrivers] = useState();
    const [initialDrivers, setInitialDrivers] = useState();
    const [isExpanded, setIsExpanded] = useState(false);
    const [isUseInModel, setIsUseInModel] = useState(false);

    const updateDriverDataByFactor = (driverData, factor) => {
      let result = [];

      if (driverData.length !== 0) {
        result.push(driverData[0])
        for (let i = 1; i < driverData.length; i++) {
          const prevAmount = driverData[i-1].amount;

          driverData[i].amount = prevAmount * factor;

          result.push(driverData[i]);
        }
      }

      return result;
    };

    useEffect(() => {
      if(!!initialFactors) {
        setDrivers(initialFactors);
        setInitialDrivers(initialFactors);
        return
      } else if (!modelResults || useInitialFactors) {
        const emptyDrivers = {};

        tableConfig.map((config) => {
          emptyDrivers[config.propName] = {
            factor: null,
            data: Array.from({length: predictionSize + 1}, (_, i) => {
              return {
                date: moment(firstOfMonthIn(i)).format('YYYY-MM-DD'),
                amount: 0,
              };
            }),
          }
        })
        setDrivers(emptyDrivers);
        setInitialDrivers(emptyDrivers);
        return
      }

      const driversData = { ...modelResults, ...{ roas: roasVector } };

      const _drivers = tableConfig.reduce((acc, config) => {
        const row = _get(driversData, config.dataPath);

        const indexDataStart =
          config.indexDataStart !== undefined
            ? config.indexDataStart
            : 12 //row.length - 12;

        const data = row.slice(indexDataStart).map((item) => ({
          date: moment({
            year: item.date.year,
            month: item.date.month - 1,
            day: item.date.day,
          }).format('YYYY-MM-DD'),
          amount: Math.abs(item.amount),
        }));
        acc[config.propName] = {
          factor: null,
          data,
        };
        return acc;
      }, {});

      if (initialFactors) {
        for (const [key, value] of Object.entries(initialFactors)) {
          const initialFactor = value.factor;
          if (!!_drivers[key]) {
            _drivers[key].factor = initialFactor;

            if (initialFactor) {
              _drivers[key].data = updateDriverDataByFactor(
                  _drivers[key].data,
                  initialFactor
              );
            }
          }
        }
      }

      setDrivers(cloneDeep(_drivers));
      setInitialDrivers(cloneDeep(_drivers));
    }, [modelResults, roasVector, initialFactors, useInitialFactors, predictionSize]);

    // useEffect(() => {
    //   onDriversChange(drivers);
    // }, [drivers]);

    useEffect(() => {
      if (isUseInModel) {
        onDriversChange(drivers);
      } else {
        onDriversChange(null);
      }
    }, [drivers, isUseInModel]);

    const getHeaderLabels = (len) => {
      return Array.from({ length: len }).map((_, index) => `MOB.${index}`);
    };

    useImperativeHandle(
      ref,
      () => {
        return {
          generateCsvData: () => {
            const headers = ['Driver', 'Factor', ...getHeaderLabels(drivers.roas.data.length)];

            const data = tableConfig.map((config) => {
              // const hasPercent = config.title.con
              return [
                config.title,
                drivers[config.propName].factor,
                ...drivers[config.propName].data.map((item) =>
                  isNaN(item.amount) ? null : item.amount.toFixed(2)
                ),
              ];
            });

            return [headers, ...data];
          },
        };
      },
      [drivers]
    );

    // if (!modelResults || !drivers) {
    //   return null;
    // }

    // if (!drivers) {
    //   return null;
    // }

    const onFactorChange = (propName, factor) => {
      setDrivers((prev) => {
        let _drivers = { ...prev };

        if (!factor) {
          _drivers[propName].data = cloneDeep(initialDrivers[propName].data);
          return _drivers;
        }

        _drivers[propName].data = updateDriverDataByFactor(
          _drivers[propName].data,
          factor
        );

        return _drivers;
      });
    };

    const extendArray = (arr, maxLength, fillValue = null) => {
      const result = [...arr];

      // let i = 0;
      while (result.length < maxLength) {
        result.push(fillValue);
        // i++;
      }

      return result;
    }
    return (
      <>
        <Flex gap={5} align={'center'}>
          <Flex align={'center'}>
            <Button
              display={'block'}
              minW={'20px'}
              h={'20px'}
              p={0}
              bg={'inherit'}
              _hover={{ bg: 'inherit' }}
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? '-' : '+'}
            </Button>
            <Text fontWeight={800}>Drivers</Text>
          </Flex>
          <Flex gap={1} align={'center'} fontSize={'sm'}>
            <Switch
              id="useInModel"
              isChecked={isUseInModel}
              onChange={(event) => {
                setIsUseInModel(event.target.checked);
              }}
            />
            <Text>Use in model </Text>
          </Flex>
        </Flex>
        {isExpanded && (
          <Box position={'relative'}>
            {!isUseInModel && <DisabledOverlay />}
            <TableContainer mt={4}>
              <Table
                variant={'simple'}
                sx={{
                  borderCollapse: 'separate',
                  borderSpacing: '0',
                }}
                __css={{
                  'td, th': {
                    padding: '5px',
                    minWidth: '100px',
                    whiteSpace: 'nowrap',
                  },
                  th: {
                    fontWeight: '800',
                    color: 'inherit',
                    borderBottom: '1px solid',
                    borderTop: '1px solid',
                    borderRight: '1px solid',
                  },
                  td: {
                    fontSize: 'sm',
                    borderBottom: '1px solid',
                    borderRight: '1px solid',
                  },
                  'td:first-of-type, th:first-of-type': {
                    borderLeft: '1px solid',
                  },
                }}
              >
                <Thead>
                  <Tr>
                    <Th position={'sticky'} left={0} bg={'white'} zIndex={1}>
                      Driver
                    </Th>
                    <Th>Factor</Th>
                    {getHeaderLabels(drivers.roas.data.length).map((label, index) => (
                      <Th key={index}>{label}</Th>
                    ))}
                  </Tr>
                </Thead>
                <Tbody>
                  {tableConfig.map((config) => (
                    <Tr key={config.propName}>
                      <Td position={'sticky'} left={0} bg={'white'} zIndex={1}>
                        {config.title}
                      </Td>
                      <Td>
                        <AnotherNumberInput
                          value={drivers[config.propName].factor}
                          variant={'inlineTable'}
                          onChange={(factor) => {
                            // onFactorChange(config.propName, factor)
                            setDrivers((prev) => {
                                const _drivers = {...prev};
                                _drivers[config.propName].factor = factor;
                                return _drivers;
                              })
                            }
                          }
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                            }
                          }}
                          onBlur={(factor) => {
                              onFactorChange(config.propName, factor)
                              setDrivers((prev) => {
                                const _drivers = {...prev};
                                _drivers[config.propName].factor = factor;
                                return _drivers;
                              })
                            }
                          }
                        />
                      </Td>
                      {
                        extendArray(drivers[config.propName].data, drivers.roas.data.length, {amount:'-'}).map((item, index) => (
                        <Td key={index}>
                          {item.amount === '-' ? ('-') :
                          (<config.element
                            variant={'inlineTable'}
                            value={item.amount}
                            precision={config.precision || 0}
                            onChange={(value) => {
                              setDrivers((prev) => {
                                const _drivers = { ...prev };
                                _drivers[config.propName].data[index].amount =
                                  value;
                                return _drivers;
                              });
                            }}
                            onKeyDown={e=> {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                              }
                            }}
                            onBlur={(value) => {
                              setDrivers((prev) => {
                                const _drivers = { ...prev };
                                _drivers[config.propName].data[index].amount =
                                    value;
                                return _drivers;
                              });
                            }}
                          />
                          )}
                        </Td>
                      ))}
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </>
    );
  }
);

CashflowModelDrivers.propTypes = {
  modelResults: PropTypes.object,
  onDriversChange: PropTypes.func,
  roasVector: PropTypes.array,
  initialFactors: PropTypes.object,
  useInitialFactors: PropTypes.bool,
  predictionSize: PropTypes.number,
};

export default CashflowModelDrivers;
